<template>
  <View direction="row" wrap="wrap" gap="sm" v-if="renterHighlightsBadges && renterHighlightsBadges.length > 0">
    <Badge
      v-for="badge of renterHighlightsBadges"
      :key="badge.label"
      :variant="badge.type"
      :label="badge.label"
      size="small"
    />
  </View>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { BadgeVariant, Badge, View } from '@tallo/design-system';
import { PropertyDto, RenterRequirementsMinimumIncome } from '@tallo/property';
import { RenterDto } from '@tallo/renter';

const props = defineProps<{ renter: RenterDto; property: PropertyDto }>();

const renterHighlightsBadges = computed<{ label: string; type: BadgeVariant }[]>(() => {
  const badges: { label: string; type: BadgeVariant }[] = [];

  const renter = props.renter;
  const requirements = props.property?.renterRequirements;
  const conditions = props.property?.leaseConditions;
  const petPolicy = props.property?.petPolicy;

  // If we have no renter data, no badges can be derived
  if (!renter) {
    return badges;
  }

  // --- Housing Voucher ---
  if (renter.hasHousingVoucher) {
    badges.push({ label: 'Has housing voucher', type: 'grey' });
  }

  // --- Pet Policy Checks ---
  if (renter.hasPets && petPolicy && !petPolicy.allowsPets) {
    badges.push({ label: 'Has pets', type: 'red' });
  }

  // If there are no requirements, we stop here as no further checks are needed
  if (!requirements) {
    return badges;
  }

  // --- Credit Checks ---
  const { creditScore } = renter;
  const { minimumCreditScore, minimumIncome } = requirements;

  if (creditScore && minimumCreditScore) {
    const renterScore = +creditScore;
    if (renterScore < minimumCreditScore) {
      badges.push({ label: 'Low credit', type: 'red' });
    } else {
      badges.push({ label: 'Pass credit requirement', type: 'green' });
    }
  }

  // --- Income Checks ---
  if (conditions?.rent && minimumIncome) {
    const minRequiredIncome = calculateMinimumIncome(conditions.rent, minimumIncome);
    const monthlyIncome = renter.monthlyIncome ? +renter.monthlyIncome : null;

    if (minRequiredIncome !== null && monthlyIncome !== null) {
      if (monthlyIncome < minRequiredIncome && !renter.hasHousingVoucher) {
        badges.push({ label: 'Low income', type: 'red' });
      } else if (monthlyIncome > minRequiredIncome) {
        badges.push({ label: 'Pass income requirement', type: 'green' });
      }
    }
  }

  // --- Move-in Date Checks ---
  if (renter.desiredMoveInDate && conditions?.desiredLeasingDate) {
    // if conditions date in the past, we set it to today
    if (new Date(conditions.desiredLeasingDate) < new Date()) {
      conditions.desiredLeasingDate = new Date().toISOString();
    }
    const desiredMoveInDate = new Date(renter.desiredMoveInDate);
    const desiredLeasingDate = new Date(conditions.desiredLeasingDate);
    const differenceInDays = (desiredMoveInDate.getTime() - desiredLeasingDate.getTime()) / (1000 * 60 * 60 * 24);

    // Check if move-in date is 30 days or more from the desired leasing date
    if (differenceInDays >= 30) {
      badges.push({ label: 'Move-in date is far', type: 'red' });
    }
  }

  return badges;
});

function calculateMinimumIncome(
  rent: number,
  minimumIncomeRequirement: RenterRequirementsMinimumIncome,
): number | null {
  if (!rent || !minimumIncomeRequirement) {
    return null; // Handle invalid inputs gracefully
  }

  switch (minimumIncomeRequirement) {
    case RenterRequirementsMinimumIncome.X1_RENT:
      return rent;
    case RenterRequirementsMinimumIncome.X2_RENT:
      return rent * 2;
    case RenterRequirementsMinimumIncome.X2_5_RENT:
      return rent * 2.5;
    case RenterRequirementsMinimumIncome.X3_RENT:
      return rent * 3;
    case RenterRequirementsMinimumIncome.X3_5_RENT:
      return rent * 3.5;
    case RenterRequirementsMinimumIncome.NO_REQUIREMENT:
      return 0;
    default:
      return null; // Unknown requirement
  }
}
</script>
